import type { ReactElement, ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { SuspenseLoader } from '../../../components';
import { useAuthContext } from '../../../contexts';

interface PermissionsGuardProps {
  children: ReactNode;
  allowedPermissions?: string[];
  userId?: string;
  ownClientId?: boolean;
  ownUserId?: boolean;
  should404?: boolean;
}

function isPermissionAllowed(required: string[], actual: string[]): boolean {
  if (required.length === 0) return true;
  return actual.some((p) => required.includes(p));
}

export const PermissionsGuard = ({
  children,
  allowedPermissions = [],
  userId,
  ownUserId = false,
  ownClientId = false,
  should404 = false
}: PermissionsGuardProps): ReactElement | null => {
  const { auth } = useAuthContext();
  const { userId: paramUserId, clientId: paramClientId } = useParams();

  if (!auth) {
    return <SuspenseLoader />;
  }

  const hasPermission = isPermissionAllowed(allowedPermissions, auth.permissions);
  const isOwnUser = ownUserId && auth.id === paramUserId;
  const isOwnClient = ownClientId && auth.clients.some((client) => client.id === paramClientId);
  const isExactUser = auth.id === userId;

  if (hasPermission || isExactUser || isOwnUser || isOwnClient) {
    return <>{children}</>;
  }

  return should404 ? <Navigate to="/404" replace /> : null;
};
